import React, { useMemo } from "react";
import {
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@twilio-paste/core";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

import AppHeader from "../AppHeader";
import OperatorsPanel from "./OperatorsPanel";
import TemplatesPanel from "./TemplatesPanel";
import { useTwilioClient } from "../../hooks/twilio/useTwilioClient";
import { actionCreators, AppState } from "../../store";
import { markSignedOut } from "../../utils/storageUtils";
import { authClient } from "../../auth";
import stylesheet from "../../styles";

const AdminPage: React.FC = () => {
  const { client, connectionState, usernameForHeader } = useTwilioClient();
  const operator = useSelector((state: AppState) => state.operator);
  const roles = useSelector((state: AppState) => state.roles);
  const dispatch = useDispatch();
  const { logout } = bindActionCreators(actionCreators, dispatch);

  const canViewAll = useMemo(() => {
    if (!operator) return false;
    const role = roles.find((r) => r.id === operator.roleId);
    return role?.canViewAll ?? false;
  }, [operator, roles]);

  if (!canViewAll) {
    return <Navigate to="/" replace />;
  }

  const onLogout = async () => {
    try {
      if (client) {
        client.removeAllListeners();
        if (typeof (client as any).shutdown === "function") {
          await (client as any).shutdown();
        }
      }
    } catch (e) {
      console.warn("Errore durante la disconnessione Twilio:", e);
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    } catch (e) {
      console.warn("Errore durante unregister service worker:", e);
    }

    logout();
    markSignedOut();

    const accounts = authClient.getAccounts();
    const account = accounts[0];

    if (!account) {
      window.location.reload();
      return;
    }

    if (authClient.getInProgress() !== "none") {
      console.warn("[MSAL] logout ignorato: interaction già in corso.");
      return;
    }

    await authClient.logoutRedirect({
      account: account as any,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return (
    <Box style={stylesheet.appWrapper}>
      <Box padding="space60" overflowY="auto" style={{ flex: 1 }}>
        <Tabs baseId="admin-tabs">
          <TabList aria-label="Pannelli di gestione">
            <Tab id="tab-operators">Operatori</Tab>
            <Tab id="tab-templates">Template</Tab>
          </TabList>
          <TabPanels>
            <TabPanel tabId="tab-operators">
              <OperatorsPanel />
            </TabPanel>
            <TabPanel tabId="tab-templates">
              <TemplatesPanel />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export default AdminPage;
